import type { ApiResponse } from '@burmazon/shared';
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
  timeout: 10_000,
});

// ─── Token management ─────────────────────────────────────────────────────────
// Restore from localStorage on page load so refresh doesn't cause logout.

let accessToken: string | null = localStorage.getItem('pos-accessToken');

type TokenSubscriber = () => void;
const tokenSubscribers = new Set<TokenSubscriber>();

export const setAccessToken = (t: string | null): void => {
  accessToken = t;
  if (t) {
    localStorage.setItem('pos-accessToken', t);
  } else {
    localStorage.removeItem('pos-accessToken');
  }
  tokenSubscribers.forEach((fn) => fn());
};

export const getAccessToken = (): string | null => accessToken;

export const subscribeToToken = (fn: TokenSubscriber): (() => void) => {
  tokenSubscribers.add(fn);
  return () => tokenSubscribers.delete(fn);
};

// ─── Logout ───────────────────────────────────────────────────────────────────

export function logout(): void {
  accessToken = null;
  localStorage.removeItem('pos-accessToken');
  localStorage.removeItem('pos-refreshToken');
  tokenSubscribers.forEach((fn) => fn());
  window.location.href = '/login';
}

// ─── Token refresh ────────────────────────────────────────────────────────────

let isRefreshing = false;
type QueueItem = { resolve: (token: string) => void; reject: (err: unknown) => void };
let refreshQueue: QueueItem[] = [];

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('pos-refreshToken');
  if (!refreshToken) return null;

  try {
    const res = await axios.post<ApiResponse<{ accessToken: string; refreshToken: string }>>(
      `${BASE_URL}/auth/refresh`,
      { refreshToken },
    );
    if (res.data.success) {
      const { accessToken: newAccess, refreshToken: newRefresh } = res.data.data;
      localStorage.setItem('pos-refreshToken', newRefresh);
      setAccessToken(newAccess);
      return newAccess;
    }
    return null;
  } catch {
    return null;
  }
}

// ─── Error extraction ─────────────────────────────────────────────────────────

function extractError(error: AxiosError): Error {
  const serverMessage = (error.response?.data as { error?: { message?: string } })?.error?.message;
  if (serverMessage) return new Error(serverMessage);

  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message.toLowerCase().includes('timeout')) {
      return new Error('Request timed out — please check your connection and try again');
    }
    return new Error('Unable to reach the server — please check your network connection');
  }

  const fallbacks: Record<number, string> = {
    400: 'Invalid request — please check your input',
    401: 'Invalid email or password',
    403: 'You do not have permission to perform this action',
    404: 'The requested resource was not found',
    409: 'This action conflicts with existing data',
    422: 'Validation failed — please check your input',
    429: 'Too many requests — please wait a moment and try again',
    500: 'A server error occurred — please try again later',
    502: 'Service temporarily unavailable — please try again',
    503: 'Service temporarily unavailable — please try again',
  };
  const status = error.response.status;
  return new Error(fallbacks[status] ?? `Request failed (${status})`);
}

// ─── Interceptors ─────────────────────────────────────────────────────────────

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

apiClient.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (
      error.response?.status === 401 &&
      !original._retry &&
      localStorage.getItem('pos-refreshToken')
    ) {
      original._retry = true;

      if (isRefreshing) {
        // Queue this request until the ongoing refresh completes
        return new Promise((resolve, reject) => {
          refreshQueue.push({
            resolve: (token) => {
              original.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(original));
            },
            reject,
          });
        });
      }

      isRefreshing = true;
      const newToken = await refreshAccessToken();
      isRefreshing = false;

      if (newToken) {
        refreshQueue.forEach((item) => item.resolve(newToken));
        refreshQueue = [];
        original.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(original);
      }

      // Refresh failed — log out
      refreshQueue.forEach((item) => item.reject(extractError(error)));
      refreshQueue = [];
      logout();
    }

    return Promise.reject(extractError(error));
  },
);

// ─── HTTP helpers ─────────────────────────────────────────────────────────────

export async function apiGet<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const res = await apiClient.get<ApiResponse<T>>(url, { params });
  if (!res.data.success) throw new Error(res.data.error.message);
  return res.data.data;
}

export async function apiPost<T>(url: string, data?: unknown): Promise<T> {
  const res = await apiClient.post<ApiResponse<T>>(url, data);
  if (!res.data.success) throw new Error(res.data.error.message);
  return res.data.data;
}

export async function apiDelete<T = null>(url: string): Promise<T> {
  const res = await apiClient.delete<ApiResponse<T>>(url);
  if (!res.data.success) throw new Error(res.data.error.message);
  return res.data.data;
}

export async function apiPut<T>(url: string, data?: unknown): Promise<T> {
  const res = await apiClient.put<ApiResponse<T>>(url, data);
  if (!res.data.success) throw new Error(res.data.error.message);
  return res.data.data;
}
